/**
 * Abgleich mit einem Ordner am PC. Läuft nur in der Windows-App (Tauri); im Browser gibt es keinen Zugriff auf Ordner.
 * Was wann passiert, plant planSync aus @handvero/shared; hier wird es ausgeführt.
 */
import { useSyncExternalStore } from "react";
import { emptyState, planSync, type FileInfo, type FolderInfo, type SyncAction, type SyncManifest, type SyncSkip, type SyncState } from "@handvero/shared";
import { api, ApiFailure, errorText, upload } from "../api";

type Invoke = (cmd: string, args?: unknown, options?: { headers?: Record<string, string> }) => Promise<unknown>;

function tauriInvoke(): Invoke | null {
  const w = window as unknown as { __TAURI_INTERNALS__?: { invoke?: Invoke } };
  return w.__TAURI_INTERNALS__?.invoke ?? null;
}

/** Läuft die Seite in der Windows-App? */
export const inWindowsApp = () => tauriInvoke() !== null;

function call<T>(cmd: string, args?: unknown, options?: { headers?: Record<string, string> }): Promise<T> {
  const invoke = tauriInvoke();
  if (!invoke) return Promise.reject(new Error("Nur in der Windows-App verfügbar."));
  return invoke(cmd, args, options).catch((err: unknown) => {
    throw new Error(typeof err === "string" ? err : errorText(err));
  }) as Promise<T>;
}

interface LocalMeta {
  size: number;
  mtime: number;
}

const pc = {
  folder: () => call<string | null>("ordner"),
  choose: () => call<string | null>("ordner_waehlen"),
  disconnect: () => call<void>("ordner_trennen"),
  open: (path?: string) => call<void>("ordner_oeffnen", { path: path ?? null }),
  list: () => call<{ files: { path: string; size: number; mtime: number }[]; dirs: string[] }>("liste"),
  read: (path: string) => call<ArrayBuffer>("lesen", { path }),
  write: (path: string, data: ArrayBuffer) => call<LocalMeta>("schreiben", new Uint8Array(data), { headers: { pfad: encodeURIComponent(path) } }),
  move: (from: string, to: string) => call<LocalMeta>("verschieben", { from, to }),
  trash: (path: string) => call<void>("papierkorb", { path }),
  mkdir: (path: string) => call<void>("ordner_anlegen", { path }),
  readState: () => call<string | null>("zustand_lesen"),
  writeState: (inhalt: string) => call<void>("zustand_schreiben", { inhalt }),
};

export interface SyncStatus {
  available: boolean;
  folder: string | null;
  running: boolean;
  /** Zeitpunkt des letzten vollständigen Abgleichs. */
  lastRun: string | null;
  /** Was beim letzten Abgleich passiert ist. */
  summary: { down: number; up: number; moved: number; trashed: number; folders: number } | null;
  /** Aktuelle Tätigkeit, z. B. „Lädt herunter: Fotos/Bild.jpg“. */
  activity: string | null;
  error: string | null;
  /** Dateien, die nicht abgeglichen werden konnten, und warum. */
  problems: SyncSkip[];
}

const INTERVAL_MS = 60_000;

let status: SyncStatus = {
  available: false,
  folder: null,
  running: false,
  lastRun: null,
  summary: null,
  activity: null,
  error: null,
  problems: [],
};
const listeners = new Set<() => void>();

function set(patch: Partial<SyncStatus>) {
  status = { ...status, ...patch };
  for (const l of listeners) l();
}

export function useSyncStatus(): SyncStatus {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => status,
  );
}

function parseState(raw: string | null, account: string): SyncState | "fremd" {
  if (!raw) return emptyState(account);
  try {
    const s = JSON.parse(raw) as SyncState;
    if (s.version !== 1 || typeof s.files !== "object") return emptyState(account);
    return s.account === account ? s : "fremd";
  } catch {
    return emptyState(account);
  }
}

async function download(fileId: string): Promise<ArrayBuffer> {
  let res: Response;
  try {
    res = await fetch(`/api/files/${fileId}/content?download=1`, { credentials: "same-origin" });
  } catch {
    throw new ApiFailure(0, "Keine Verbindung zum Server.");
  }
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiFailure(res.status, data.error ?? "Herunterladen fehlgeschlagen.");
  }
  return res.arrayBuffer();
}

async function uploadLocal(folderId: string, path: string): Promise<FileInfo> {
  const data = await pc.read(path);
  const form = new FormData();
  form.append("file", new Blob([data]), path.slice(path.lastIndexOf("/") + 1));
  return upload<FileInfo>(`/api/folders/${folderId}/files`, form);
}

const label = (path: string) => path.split("/").slice(-2).join("/");

/** Führt eine geplante Aktion aus und trägt das Ergebnis in den Zustand ein. */
async function run(action: SyncAction, state: SyncState, count: NonNullable<SyncStatus["summary"]>) {
  switch (action.type) {
    case "download": {
      set({ activity: `Lädt herunter: ${label(action.path)}` });
      const meta = await pc.write(action.path, await download(action.fileId));
      state.files[action.fileId] = { path: action.path, projectId: action.projectId, ...meta };
      count.down++;
      return;
    }
    case "move": {
      const meta = await pc.move(action.from, action.to);
      const known = state.files[action.fileId]!;
      state.files[action.fileId] = { ...known, path: action.to, ...meta };
      count.moved++;
      return;
    }
    case "trash":
      await pc.trash(action.path);
      delete state.files[action.fileId];
      count.trashed++;
      return;
    case "adopt":
      state.files[action.fileId] = { path: action.path, size: action.size, mtime: action.mtime, projectId: action.projectId };
      return;
    case "forget":
      delete state.files[action.fileId];
      return;
    case "upload": {
      set({ activity: `Lädt hoch: ${label(action.path)}` });
      const saved = await uploadLocal(action.folderId, action.path);
      state.files[saved.id] = { path: action.path, size: action.size, mtime: action.mtime, projectId: action.projectId };
      count.up++;
      return;
    }
    case "replace": {
      set({ activity: `Lädt hoch: ${label(action.path)}` });
      const saved = await uploadLocal(action.folderId, action.path);
      delete state.files[action.fileId];
      state.files[saved.id] = { path: action.path, size: action.size, mtime: action.mtime, projectId: action.projectId };
      // Die alte Fassung in HandVero entfernen. Darf die Person das nicht, bleiben beide erhalten.
      await api.del(`/api/files/${action.fileId}`).catch(() => undefined);
      count.up++;
      return;
    }
    case "mkdir":
      await pc.mkdir(action.path);
      return;
    case "createFolder": {
      await api.post<FolderInfo>(`/api/projects/${action.projectId}/folders`, { name: action.name, parentId: action.parentId });
      count.folders++;
      return;
    }
  }
}

let timer: number | undefined;
let pending: Promise<void> | null = null;

/** Ein Abgleich. Läuft schon einer, wird auf ihn gewartet. */
export function syncNow(): Promise<void> {
  pending ??= syncOnce().finally(() => {
    pending = null;
  });
  return pending;
}

async function syncOnce() {
  if (!inWindowsApp()) return;
  const folder = await pc.folder().catch(() => null);
  set({ available: true, folder });
  if (!folder) return;
  set({ running: true, error: null, activity: "Vergleicht …" });
  const count = { down: 0, up: 0, moved: 0, trashed: 0, folders: 0 };
  const problems: SyncSkip[] = [];
  try {
    // Neue Ordner brauchen einen zweiten Durchgang, damit die Dateien darin gleich mitkommen.
    for (let round = 0; round < 4; round++) {
      const manifest = await api.get<SyncManifest>("/api/sync/manifest");
      const state = parseState(await pc.readState(), manifest.account);
      if (state === "fremd") {
        throw new Error("Dieser Ordner wird schon für ein anderes HandVero-Konto abgeglichen. Bitte wählen Sie einen anderen Ordner.");
      }
      const plan = planSync(manifest, state, await pc.list());
      if (round === 0 || plan.actions.length) problems.splice(0, problems.length, ...plan.skipped);
      if (!plan.actions.length) break;
      let saved = 0;
      for (const action of plan.actions) {
        try {
          await run(action, state, count);
        } catch (err) {
          if (err instanceof ApiFailure && err.status === 401) throw err;
          const path = "path" in action ? action.path : action.type === "move" ? action.from : "";
          problems.push({ path, reason: errorText(err) });
        }
        // Zwischendurch speichern, damit ein Abbruch nichts doppelt hoch- oder herunterlädt.
        if (++saved % 10 === 0) await pc.writeState(JSON.stringify(state));
      }
      await pc.writeState(JSON.stringify(state));
      if (!plan.actions.some((a) => a.type === "createFolder")) break;
    }
    set({ lastRun: new Date().toISOString(), summary: count, problems });
  } catch (err) {
    set({ error: errorText(err), problems });
  } finally {
    set({ running: false, activity: null });
  }
}

/** Startet den regelmäßigen Abgleich (jede Minute und wenn das Fenster wieder nach vorn kommt). */
export function startPcSync(): () => void {
  if (!inWindowsApp()) return () => undefined;
  set({ available: true });
  void syncNow();
  timer = window.setInterval(() => void syncNow(), INTERVAL_MS);
  const onFocus = () => void syncNow();
  window.addEventListener("focus", onFocus);
  return () => {
    window.clearInterval(timer);
    window.removeEventListener("focus", onFocus);
  };
}

export async function chooseFolder(): Promise<void> {
  const folder = await pc.choose();
  if (folder) {
    set({ folder, summary: null, problems: [], error: null, lastRun: null });
    await syncNow();
  }
}

export async function disconnectFolder(): Promise<void> {
  await pc.disconnect();
  set({ folder: null, summary: null, problems: [], error: null, lastRun: null });
}

export const openFolder = (path?: string) => pc.open(path);
